/**
 * Static feature flags evaluated at build time and PostHog remote flag keys.
 * @module
 */
import { env } from "@/lib/env";

/** Build-time feature flag states for static feature gating. */
export type FeatureFlag = "admin" | "beta" | "on" | "off";

/** PostHog remote feature flag keys — used with usePostHogFeatureFlag(). */
export const POSTHOG_FLAGS = {
  MANUALLY_ADD_PLAYER: "manually-add-player",
  MANUAL_PLAYER_TRANSFER_DATA: "manual-player-transfer-data",
  BASIC_STATS_TAB: "basic-stats-tab",
  SQUAD_ROLE_GATING: "squad-role-gating",
  SQUAD_CONTRACTS_TAB: "squad-contracts-tab",
  /**
   * Whether the Squad **Compliance** tab exists at all — the tab itself, its
   * Objectives button, its KPI tiles, and the Compliance routes surfaced from
   * action modals (SE-8003).
   *
   * Read with a BARE key — no {@link withEnvSuffix}. Per-environment
   * targeting is done in PostHog itself, via a release-condition group
   * filtering the `environment` person property (set on every `identify()`
   * call by `usePostHogIdentify`, and as a super-property on every event by
   * `setPostHogInstance`), the same pattern
   * {@link POSTHOG_FLAGS.HOME_CREATE_NOTE_QUICK_ACTION} established (SE-7864)
   * and {@link POSTHOG_FLAGS.COMPLIANCE_REAL_INSIGHTS} followed (SE-8385).
   * That pattern only needed inventing once `environment` was already being
   * sent on every event/identify — before that, dev/staging/production
   * sharing one PostHog project+key meant per-env state could not live in a
   * single flag, which is what `withEnvSuffix` originally worked around.
   *
   * SEPARATE from `COMPLIANCE_TAB_BETA`, which controls whether the (already
   * existing) tab still wears its BETA pill — this flag governs existence,
   * that one governs beta chrome.
   */
  SQUAD_FINANCIALS_TAB: "squad-financials-tab",
  PLAYER_DOWNLOAD_REPORT: "player-download-report",
  /**
   * Gates the MR-2.0 match-report surfaces, quick capture first (SE-6980).
   *
   * Deliberately a SEPARATE flag from the backend's AWS AppConfig
   * `matchReportV2`, which gates the GraphQL operations — the backend has no
   * PostHog. The two can disagree, so a client with this flag on can still
   * meet a `FEATURE_DISABLED` GraphQL error, and that error path is reachable
   * in practice rather than only in theory.
   */
  MATCH_REPORT_V2: "match-report-v2",
  /**
   * Gates the AI match-level summary on the submitted report view (SE-6989).
   *
   * Separate from MATCH_REPORT_V2 (which gates the whole surface, including
   * its route) so the summary can roll out — or be pulled — independently of
   * the report view itself.
   */
  MATCH_REPORT_AI_SUMMARY: "match-report-ai-summary",
  /**
   * Gates the create-note quick action on Home (SE-6845) at both of its render
   * sites — desktop `HomeScreenView` and mobile `MobileHomeScreenView` (SE-7864).
   *
   * Deliberately a SEPARATE flag from Home itself and from the desktop "+" menu
   * (`useAddMenuActions`), which is an independent, already-shipped entry point
   * to the same note-creation path — so the Home quick action can roll out, or
   * be pulled, independently of either. It gates the two RENDER SITES only, never
   * the shared `useOpenCreateNoteForPlayer` reader, so the player-page note flow
   * is unaffected whatever this flag says.
   *
   * Fails CLOSED: an absent, unreadable or still-loading flag hides the action.
   * See `useCreateNoteQuickActionEnabled` for why, and for the dev-time warning
   * that keeps "flag missing" distinguishable from "flag off".
   */
  HOME_CREATE_NOTE_QUICK_ACTION: "home-create-note-quick-action",
  /**
   * Whether the Squad **Compliance** tab still wears the BETA pill (SE-8200).
   *
   * A KILL-SWITCH, not a rollout gate, and the direction matters: the badge
   * shows unless this reads an explicit `false`. So the pill is visible today
   * with nothing configured in PostHog, and dropping it when the tab leaves
   * beta is a flag flip rather than a deploy — which is the whole reason it is
   * a flag. Same optimistic shape as `isNavItemVisible`, for the same reason:
   * a rollout toggle may not blank UI while it is still loading.
   *
   * SEPARATE from `SQUAD_FINANCIALS_TAB`, which gates whether the tab EXISTS.
   * One flag cannot answer both "may this user see the tab" and "is the tab
   * still beta" — turning the tab on would then also declare it stable.
   *
   * Separate from MATCHES_TAB_BETA too: the two surfaces are different
   * features and will stabilise on different days, so a shared flag would
   * force them out of beta together.
   */
  COMPLIANCE_TAB_BETA: "compliance-tab-beta",
  /**
   * Whether the Scouting **Matches** tab still wears the BETA pill (SE-8200).
   *
   * Same kill-switch semantics as {@link POSTHOG_FLAGS.COMPLIANCE_TAB_BETA},
   * and separate from `MATCH_REPORT_V2`, which gates whether the tab exists at
   * all.
   */
  MATCHES_TAB_BETA: "matches-tab-beta",
  /**
   * Whether the Compliance **actionable-insights** section renders at all —
   * header, badge, everything — or is hidden outright. No ticket yet.
   *
   * A KILL-SWITCH, not a rollout gate, same direction as
   * {@link POSTHOG_FLAGS.COMPLIANCE_TAB_BETA}: the section shows unless this
   * reads an explicit `false`. So it stays visible today with nothing
   * configured in PostHog, and pulling it later is a flag flip rather than a
   * deploy — which is the whole reason it is a flag.
   *
   * Read with a BARE key — no {@link withEnvSuffix} — same as
   * {@link POSTHOG_FLAGS.SQUAD_FINANCIALS_TAB}: dev/staging/production
   * share one PostHog project, and any per-environment targeting this ever
   * needs belongs in a PostHog release condition on the `environment` person
   * property, not in the key itself.
   *
   * Because "absent" and "off" must read differently for a kill-switch —
   * PostHog answers a single-flag lookup for an unknown key with a real
   * boolean `false`, not `undefined` — this is read through
   * `usePostHogFeatureFlagMap` (checking the flag's presence in the loaded
   * map), not the plain `usePostHogFeatureFlag` getter. See that hook's doc
   * and `COMPLIANCE_TAB_BETA`'s own reader, `useBetaBadgeVisible`, for the
   * measured reason.
   */
  COMPLIANCE_INSIGHTS_SECTION: "compliance-insights-section",
} as const;

/**
 * Environment suffix appended by {@link withEnvSuffix}.
 */
type PostHogFlagEnvSuffix = "dev" | "staging" | "production";

/**
 * Resolves the suffix `withEnvSuffix` appends, from `EXPO_PUBLIC_ENV`.
 *
 * Only `"staging"` and `"production"` get their own suffix. Everything
 * else — `"dev"`, `"localhost"`, or an unset `EXPO_PUBLIC_ENV` — folds into
 * `"dev"`, so a plain local run or a misconfigured build addresses the dev
 * key rather than a fourth, never-configured one.
 * @returns The suffix for the current build's environment.
 */
const resolvePostHogFlagEnvSuffix = (): PostHogFlagEnvSuffix => {
  if (env.EXPO_PUBLIC_ENV === "staging") return "staging";
  if (env.EXPO_PUBLIC_ENV === "production") return "production";
  return "dev";
};

/**
 * Appends an environment suffix to a PostHog flag base key (SE-8003).
 *
 * dev/staging/production currently share ONE PostHog project — `eas.json`
 * sets `EXPO_PUBLIC_POSTHOG_API_KEY`/`EXPO_PUBLIC_POSTHOG_HOST` once on the
 * `base` build profile, not per environment — so a single flag key cannot
 * carry independent on/off state per environment through PostHog config
 * alone. This is a targeted workaround for exactly the flags that call it,
 * not a general convention: SE-8010 tracks the real fix (per-environment
 * PostHog projects) for every flag. Do not apply this to a flag unless a
 * ticket has scoped it in, the same way SE-8200 scoped in
 * {@link POSTHOG_FLAGS.MATCHES_TAB_BETA} (via {@link useBetaBadgeVisible}).
 *
 * Every call site reading the same base key MUST wrap it with this helper —
 * a bare `POSTHOG_FLAGS.MATCHES_TAB_BETA` read left un-suffixed would
 * silently disagree with the suffixed ones about which environment's flag
 * state it is honoring.
 * @param baseKey - The un-suffixed PostHog flag key, e.g.
 * `POSTHOG_FLAGS.MATCHES_TAB_BETA`.
 * @returns `` `${baseKey}-dev` ``, `` `${baseKey}-staging` ``, or
 * `` `${baseKey}-production` ``, per {@link resolvePostHogFlagEnvSuffix}.
 */
export const withEnvSuffix = (baseKey: string): string =>
  `${baseKey}-${resolvePostHogFlagEnvSuffix()}`;

export const SCENARIO_TESTING: FeatureFlag = "on";
export const DATA_UPLOAD: FeatureFlag = "on";

export const REDIRECT_FOR_MAINTENANCE: FeatureFlag = "off";

export const DASHBOARDS: FeatureFlag = "on";

export const SHOWCASE: FeatureFlag = "on";

export const TALK_TO_YOUR_DATA: FeatureFlag = "admin";
export const ASSISTANT: FeatureFlag = "admin";

export const ASK_GEMINI_RESEARCH_TOGGLE: FeatureFlag = "off";

export const MY_ROSTER_KANBAN: FeatureFlag = "on";

/**
 * Flips the player-kanban surface to PIPELINE-type boards (SE-6071) for
 * Manager+ users once the SE-5505 boards→custom-pipelines migration has run.
 * Deliberately enabled — production now gets the pipeline surface instead of
 * the previous watchlist/roster behavior.
 */
export const PIPELINE_SURFACE: FeatureFlag = "on";

export const KANBAN_BOARD_SHARING: FeatureFlag = "on";

export const VOICE_DICTATION: FeatureFlag = "on";

export const PLAYER_DETAIL_SIDEBAR: FeatureFlag = "off";

/**
 * The single global kill-switch for UI-catalog localization (SE-6507).
 *
 * "off" does NOT unmount the i18n provider and does NOT stub the translate
 * function to identity — both of those render raw keys, which is the forbidden
 * failure. Off instead pins the runtime to `lng: "en_US"` +
 * `supportedLngs: ["en_US"]`, so every string still resolves through the
 * ordinary lookup and renders its English catalog value. Overridable at build
 * time by EXPO_PUBLIC_I18N_ENABLED.
 *
 * Distinct from the auto-translate flag that gates Bedrock call volume; the two
 * gate different things and their enablement dates are independent.
 */
export const UI_LOCALIZATION: FeatureFlag = "on";

/**
 * Gates defaulting a scout report to the viewer's language, and the automatic
 * `translateScoutReport` call that default implies (SE-6510).
 *
 * **This is NOT the i18n kill-switch.** {@link UI_LOCALIZATION} decides whether
 * the app's own catalog strings may render in a non-English locale — pure
 * client-side string lookup, zero cost per open. This flag decides whether
 * opening a report may spend a Bedrock invocation translating its body. They
 * gate different things, they fail in different ways, and their enablement
 * dates are independent; conflating them would mean pulling every localized
 * label to stop a translation-provider incident, or vice versa.
 *
 * Ships `"off"`. Turning it on in production is gated on SE-5466 (the Bedrock
 * hardening pass, still ON STG) reaching production — that path has no timeout,
 * circuit breaker or rate limit of its own, and this flag is what stands
 * between an incident there and every report open in the product.
 * Overridable at build time by `EXPO_PUBLIC_SCOUT_REPORT_AUTO_TRANSLATE`.
 */
export const SCOUT_REPORT_AUTO_TRANSLATE: FeatureFlag = "off";
